import React, { RefObject, useEffect, useReducer } from 'react'
import { TooltipDictionary } from '../useRegionTooltipContent'
import getRelativeCoordinates from '../utils/getRelativeCoordinates'
import {
  ActionType,
  initialState,
  reducer,
  RegionHoverPosition
} from './reducer'

type UseRegionHoverProps = {
  svgRef: RefObject<SVGSVGElement>
  containerRef: RefObject<HTMLDivElement>
  tooltips: TooltipDictionary
  enabled?: boolean
}

function setRegionOutlineFilter(regionGroup: SVGGElement) {
  regionGroup.setAttribute('filter', 'url(#outline-filter)')
}

function removeRegionOutlineFilter(regionGroup: SVGGElement) {
  if (regionGroup.getAttribute('filter') === 'url(#outline-filter)') {
    regionGroup.removeAttribute('filter')
  }
}

function getFormattedId(id: string) {
  return id.replace('regiao-', '').replace('-', '').toLowerCase()
}

const useRegionHover = ({
  svgRef,
  containerRef,
  tooltips,
  enabled = false
}: UseRegionHoverProps) => {
  const [{ regionHoverPosition, regionKey }, dispatch] = useReducer(
    reducer,
    initialState
  )

  const storedRegionsHoverPositions = React.useRef<{
    [x: string]: RegionHoverPosition
  }>({
    norte: null,
    nordeste: null,
    centrooeste: null,
    sul: null,
    sudeste: null
  })

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !enabled) return

    const containerSize = { width: 0, height: 0 }

    const containerObserver = new ResizeObserver(([chartDiv]) => {
      const { height = 0, width = 0 } = chartDiv.contentRect || {}

      containerSize.height = height
      containerSize.width = width
    })

    if (containerRef.current) {
      containerObserver.observe(containerRef.current)
    }

    // Select all regions by id pattern "regiao-*"
    const regionGroups =
      svgRef.current.querySelectorAll<SVGGElement>('[id^="regiao-"]')

    // Initialize stroke styles on mount
    regionGroups.forEach((regionGroup) => {
      regionGroup.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        const fillColor = getComputedStyle(path).fill
        path.style.stroke = fillColor
        path.style.strokeWidth = '1px'
      })
    })

    function handleMouseEnter(this: SVGGElement) {
      const id = getFormattedId(this.getAttribute('id') || '')

      const tooltipEstimatedHeight = tooltips[id]?.estimatedHeight || 0

      const coordinates = getRelativeCoordinates(
        this,
        containerSize,
        tooltipEstimatedHeight
      )

      storedRegionsHoverPositions.current[id] = coordinates

      dispatch({ type: ActionType.SET_REGION_KEY, payload: id })

      dispatch({
        type: ActionType.SET_REGION_HOVER_POSITION,
        payload: coordinates
      })

      setRegionOutlineFilter(this)
    }

    function handleMouseLeave(this: SVGGElement) {
      dispatch({ type: ActionType.SET_REGION_KEY, payload: null })

      dispatch({ type: ActionType.SET_REGION_HOVER_POSITION, payload: null })

      // Reset stroke style to fill color stroke + 1px
      this.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        const fillColor = getComputedStyle(path).fill
        path.style.stroke = fillColor
        path.style.strokeWidth = '1px'
      })

      removeRegionOutlineFilter(this)
    }

    function handleMouseEnterLabel(this: SVGGElement) {
      const id = getFormattedId(this.getAttribute('id') || '')

      const regionGroup =
        svgRef.current?.querySelector<SVGGElement>(`[id=regiao-${id}]`) || null

      if (!regionGroup) return

      const tooltipEstimatedHeight = tooltips[id]?.estimatedHeight || 0

      dispatch({ type: ActionType.SET_REGION_KEY, payload: id })

      dispatch({
        type: ActionType.SET_REGION_HOVER_POSITION,
        payload:
          storedRegionsHoverPositions.current[id] ||
          getRelativeCoordinates(this, containerSize, tooltipEstimatedHeight)
      })

      setRegionOutlineFilter(regionGroup)
    }

    function handleMouseLeaveLabel(this: SVGGElement) {
      const id = getFormattedId(this.getAttribute('id') || '')

      const regionGroup =
        svgRef.current?.querySelector<SVGGElement>(`[id=regiao-${id}]`) || null

      if (!regionGroup) return

      dispatch({ type: ActionType.SET_REGION_KEY, payload: null })

      dispatch({ type: ActionType.SET_REGION_HOVER_POSITION, payload: null })

      regionGroup.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        const fillColor = getComputedStyle(path).fill
        path.style.stroke = fillColor
        path.style.strokeWidth = '1px'
      })
    }

    regionGroups.forEach((region) => {
      region.addEventListener('mouseenter', handleMouseEnter)
      region.addEventListener('mouseleave', handleMouseLeave)
    })

    const labels = svgRef.current.querySelectorAll<SVGGElement>('text')

    labels.forEach((label) => {
      label.addEventListener('mouseenter', handleMouseEnterLabel)
      label.addEventListener('mouseleave', handleMouseLeaveLabel)
    })

    return () => {
      regionGroups.forEach((region) => {
        region.removeEventListener('mouseenter', handleMouseEnter)
        region.removeEventListener('mouseleave', handleMouseLeave)
      })

      labels.forEach((label) => {
        label.removeEventListener('mouseenter', handleMouseEnterLabel)
        label.removeEventListener('mouseleave', handleMouseLeaveLabel)
      })

      containerObserver?.disconnect()
    }
  }, [svgRef, containerRef, enabled, tooltips])

  return {
    regionKey,
    tooltipCoordinates: regionHoverPosition
  }
}

export default useRegionHover
